import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { useBranches } from "../hooks/use-branches";
import { useRunsCiRunIds } from "../hooks/use-runs-ci-run-ids";
import { TestRunStatusList } from "../types";
import { SearchBar } from "./search-bar";

// Helper to get CI provider display name from ci_run_id
function getCiProviderLabel(ciRunId: string): string {
  if (ciRunId.startsWith("gh-")) return "GitHub";
  if (ciRunId.startsWith("gl-")) return "GitLab";
  if (ciRunId.startsWith("cci-")) return "CircleCI";
  if (ciRunId.startsWith("jenkins-")) return "Jenkins";
  if (ciRunId.startsWith("azdo-")) return "Azure";
  if (ciRunId.startsWith("travis-")) return "Travis";
  if (ciRunId.startsWith("bk-")) return "Buildkite";
  return "CI";
}

interface RunsFiltersProps {
  searchQuery: string;
  statusFilter: string;
  branchFilter?: string;
  ciRunIdFilter?: string;
  onSearchChange: (value: string) => void;
  onStatusChange: (value: string) => void;
  onBranchChange?: (value: string) => void;
  onCiRunIdChange?: (value: string) => void;
  searchPlaceholder?: string;
  showStatusFilter?: boolean;
  showBranchFilter?: boolean;
}

export const RunsFilters = ({
  searchQuery,
  statusFilter,
  branchFilter = "",
  ciRunIdFilter = "",
  onSearchChange,
  onStatusChange,
  onBranchChange,
  onCiRunIdChange,
  searchPlaceholder = "Search by name, status, or ID...",
  showStatusFilter = true,
  showBranchFilter = true,
}: RunsFiltersProps) => {
  const { data: branches = [] } = useBranches();
  const { data: ciRunIds = [], isLoading: isLoadingCiRunIds } = useRunsCiRunIds();
  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <Box sx={{ flex: 1 }}>
        <SearchBar
          size="small"
          value={searchQuery}
          onChange={onSearchChange}
          label={searchPlaceholder}
          sx={{
            width: "100%",
            "& .MuiInputLabel-root": {
              fontSize: "0.8rem",
              color: "text.secondary",
            },
            "& .MuiOutlinedInput-input": { fontSize: "0.8rem" },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "divider",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "info.dark",
            },
          }}
        />
      </Box>
      {showStatusFilter && (
        <Box>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel
              sx={{
                fontSize: "0.8rem",
                color: "text.secondary",
              }}
            >
              Status
            </InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => onStatusChange(e.target.value)}
              sx={{
                "& .MuiSelect-select": {
                  fontSize: "0.8rem",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "divider",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "info.dark",
                },
              }}
            >
              <MenuItem value="">All Statuses</MenuItem>
              {TestRunStatusList.map((status) => (
                <MenuItem key={status} value={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, " ")}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}
      {showBranchFilter && branches.length > 0 && onBranchChange && (
        <Box>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel
              sx={{
                fontSize: "0.8rem",
                color: "text.secondary",
              }}
            >
              Branch
            </InputLabel>
            <Select
              value={branchFilter}
              label="Branch"
              onChange={(e) => onBranchChange(e.target.value)}
              sx={{
                "& .MuiSelect-select": {
                  fontSize: "0.8rem",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "divider",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "info.dark",
                },
              }}
            >
              <MenuItem value="">All Branches</MenuItem>
              {branches.map((branch) => (
                <MenuItem key={branch} value={branch}>
                  {branch}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}
      {onCiRunIdChange && (
        <Box sx={{ minWidth: 200 }}>
          <Autocomplete
            size="small"
            freeSolo
            options={ciRunIds}
            value={ciRunIdFilter || null}
            loading={isLoadingCiRunIds}
            onChange={(_, newValue) => {
              onCiRunIdChange(newValue || "");
            }}
            onInputChange={(_, newInputValue, reason) => {
              // Only update on user input, not on selection
              if (reason === "input") {
                onCiRunIdChange(newInputValue);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="CI Run ID"
                placeholder="e.g., gh-12345-1"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoadingCiRunIds && <CircularProgress color="inherit" size={18} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                sx={{
                  "& .MuiInputLabel-root": {
                    fontSize: "0.8rem",
                    color: "text.secondary",
                  },
                  "& .MuiOutlinedInput-input": { fontSize: "0.8rem" },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "divider",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "info.dark",
                  },
                }}
              />
            )}
            renderOption={(props, option) => {
              const { key, ...otherProps } = props;
              return (
                <Box
                  component="li"
                  key={key}
                  {...otherProps}
                  sx={{ display: "flex", gap: 1, alignItems: "center" }}
                >
                  <Chip
                    label={getCiProviderLabel(option)}
                    size="small"
                    sx={{ fontSize: "0.7rem" }}
                  />
                  <span style={{ fontSize: "0.8rem" }}>{option}</span>
                </Box>
              );
            }}
            noOptionsText="No CI runs found"
            loadingText="Loading CI runs..."
          />
        </Box>
      )}
    </Stack>
  );
};
